import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import {
  getFeaturedArticles,
  getHotArticles,
  getLatestArticles,
  getTopicArticles,
} from "@/utils/news";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import HotRanking from "@/pages/home/components/HotRanking";
import SectionHeading from "@/components/feature/SectionHeading";
import ArticleListItem from "@/components/feature/ArticleListItem";
import TopicCard from "@/components/feature/TopicCard";

export default function Home() {
  usePageMeta({
    title: "黑料吃瓜网-最新娱乐八卦新闻与热点资讯平台",
    description:
      "黑料吃瓜网提供最新最热的娱乐八卦新闻，及时报道明星动态和热点事件，涵盖影视、明星绯闻及社会热点，满足广大网友的信息需求。",
    keywords: "黑料吃瓜网,娱乐八卦,明星新闻,热点资讯,最新八卦,娱乐动态,明星绯闻",
  });

  const slides = getFeaturedArticles(4);
  const latest = getLatestArticles(6);
  const hot = getHotArticles(5);
  const ticker = getLatestArticles(6);

  return (
    <>
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 h-11 flex items-center gap-3">
          <span className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-primary-600">
            <i className="ri-flashlight-fill" />
            快讯
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-8">
              {[...ticker, ...ticker].map((item, index) => (
                <Link
                  key={`${item.id}-${index}`}
                  to={`/detail/${item.id}`}
                  className="group flex items-center gap-2 text-xs md:text-[13px] text-foreground-700 whitespace-nowrap cursor-pointer"
                >
                  <span className="w-1 h-1 rounded-full bg-accent-500" />
                  <span className="group-hover:text-primary-600 transition-colors">{item.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-5 md:pt-7">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <div className="lg:col-span-2">
            <HeroCarousel slides={slides} />
          </div>
          <div className="hidden lg:block">
            <HotRanking articles={hot} />
          </div>
        </div>

        <div className="mt-4 md:mt-5">
          <CategoryQuickLinks />
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_336px] gap-6 lg:gap-8 items-start">
          <div>
            <SectionHeading
              title="最新资讯"
              subtitle="按发布时间排序，每三小时更新一次"
              actionLabel="查看全部娱乐新闻"
              actionTo="/category/entertainment"
            />
            <div className="bg-background-50 border border-background-200 rounded-lg px-4 md:px-6">
              {latest.map((article) => (
                <ArticleListItem key={article.id} article={article} />
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between px-1">
              <p className="text-xs text-foreground-500">已展示最近 6 条，更多内容请前往分类页</p>
              <Link
                to="/category/entertainment"
                className="text-xs md:text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                浏览全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="lg:hidden">
              <HotRanking articles={hot} />
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-primary-500" />
                热门标签
              </h3>
              <div className="mt-3.5 flex flex-wrap gap-2">
                {["影视", "情感", "片场", "行业", "音乐", "综艺", "人物", "时尚"].map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary-950 rounded-lg p-4 md:p-5">
              <p className="text-[11px] tracking-[0.2em] text-accent-300 uppercase">Editor's Pick</p>
              <h3 className="mt-2 font-heading text-lg font-bold text-background-50 leading-snug">
                娱乐行业正在被重新划线
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                版权、票务、应援集资，这些曾经模糊的地带正在被明确规则覆盖。三篇现场记录，帮你看清边界在哪。
              </p>
              <Link
                to="/topic/topic-copyright-rules"
                className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-accent-500 text-foreground-950 text-xs whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                阅读专题
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-apps-line text-primary-500" />
                栏目速览
              </h3>
              <ul className="mt-3 space-y-2.5">
                {categories.map((category) => (
                  <li key={category.id}>
                    <Link
                      to={`/category/${category.id}`}
                      className="group flex items-start gap-2.5 cursor-pointer"
                    >
                      <i className={`${category.icon} text-foreground-400 mt-0.5`} />
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                          {category.name}
                        </span>
                        <span className="block mt-0.5 text-[11px] text-foreground-500 line-clamp-1">
                          {category.description}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      <section className="w-full bg-background-200/60 border-y border-background-300/50 py-10 md:py-14">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <SectionHeading
            title="热门专题推荐"
            subtitle="按主题聚合深度内容，一次读完一件事的来龙去脉"
            actionLabel="查看全部专题"
            actionTo="/topic/topic-drama-2026"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {topics.map((topic) => (
              <div key={topic.id} className="flex flex-col">
                <TopicCard topic={topic} />
                <div className="mt-2.5 flex items-start gap-2 px-1">
                  <i className="ri-quote-text text-primary-400 mt-0.5" />
                  <p className="text-xs leading-relaxed text-foreground-600 line-clamp-2">{topic.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.24em] text-primary-600 uppercase">About Us</p>
            <h2 className="mt-2 font-heading text-2xl md:text-[32px] font-bold leading-snug text-foreground-950">
              关于黑料吃瓜网
            </h2>
            <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-foreground-700">
              我们是一个专注娱乐八卦与热点资讯的编辑团队，每天追踪影视、音乐、综艺的一线动态，也关注绯闻背后的事实边界。
              不追噱头，不编造细节，未经核实的信息一律标注来源。
            </p>
            <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-foreground-600">
              全站内容目前为演示示例，人物与事件均为虚构，用于展示站点的信息结构与阅读体验。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: "18", label: "在库资讯" },
                { value: "4", label: "深度专题" },
                { value: "3", label: "内容栏目" },
              ].map((item) => (
                <div key={item.label} className="px-3 py-3 rounded-md bg-background-50 border border-background-200">
                  <p className="font-heading text-xl md:text-2xl font-bold text-primary-600">{item.value}</p>
                  <p className="mt-0.5 text-[11px] md:text-xs text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              了解我们
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200 order-first lg:order-last">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20newsroom%20desk%20from%20above%20with%20stacked%20newspapers%2C%20a%20vintage%20camera%2C%20coffee%20cup%20and%20notebooks%2C%20soft%20window%20light%2C%20cream%20and%20maroon%20palette%2C%20quiet%20magazine%20still%20life%2C%20no%20people&width=900&height=675&seq=home-about-studio&orientation=landscape"
              alt="黑料吃瓜网编辑部工作台"
              title="黑料吃瓜网 - 编辑部"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute left-4 bottom-4 px-3 py-2 rounded-md bg-background-50/95">
              <p className="text-[11px] text-foreground-600">编辑部 · 每日三次更新</p>
              <p className="mt-0.5 text-xs font-medium text-foreground-900">理性吃瓜，先看来源</p>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 shrink-0 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-search-eye-line text-xl" />
            </span>
            <div>
              <h3 className="font-heading text-base font-bold text-foreground-950">想找某位明星或某部剧？</h3>
              <p className="mt-1 text-xs text-foreground-600">
                站内搜索支持标题、摘要、标签与正文关键词匹配，也可以按相关度和时间排序。
              </p>
            </div>
          </div>
          <Link
            to="/search"
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md border border-primary-300 text-primary-600 text-sm whitespace-nowrap hover:bg-primary-50 transition-colors cursor-pointer"
          >
            <i className="ri-search-line" />
            去搜索
          </Link>
        </div>
      </div>

      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-10 md:pt-14">
        <SectionHeading title="专题内最新" subtitle="从四个专题中各挑一篇，快速感受站点内容" compact />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {topics.map((topic) => {
            const first = getTopicArticles(topic)[0];
            if (!first) return null;
            return (
              <Link
                key={topic.id}
                to={`/detail/${first.id}`}
                className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="relative w-full aspect-[16/10] bg-background-200 overflow-hidden">
                  <img
                    src={first.cover}
                    alt={first.title}
                    title={`${first.title} - 黑料吃瓜网`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-3.5">
                  <p className="text-[10px] text-primary-600 mb-1.5">{topic.title}</p>
                  <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {first.title}
                  </h3>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}